import { createClient } from '@supabase/supabase-js';

/**
 * Lightweight error logger that inserts errors directly into the error_log table.
 * Uses a standalone Supabase client (no hooks) so it can be used outside React.
 * Batches errors and rate-limits to avoid spam.
 */

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL || '',
  process.env.REACT_APP_SUPABASE_ANON_KEY || ''
);

type ErrorSource = 'frontend' | 'edge_function';

interface ErrorEntry {
  source: ErrorSource;
  type: string;
  message: string;
  stack_trace?: string;
  user_id?: string;
  metadata?: Record<string, unknown>;
}

let currentUserId: string | null = null;

/** Call once after auth to attach user_id to all subsequent error logs. */
export const setErrorLoggerUserId = (userId: string | null) => {
  currentUserId = userId;
};

const MAX_ERRORS_PER_MINUTE = 10;
const BATCH_INTERVAL_MS = 5000;
const DEDUP_WINDOW_MS = 60000;

const errorQueue: ErrorEntry[] = [];
let errorCountThisMinute = 0;
let minuteResetTimer: ReturnType<typeof setTimeout> | null = null;
let batchTimer: ReturnType<typeof setTimeout> | null = null;
const recentErrorKeys = new Set<string>();
let dedupeCleanupTimer: ReturnType<typeof setTimeout> | null = null;

const resetMinuteCounter = () => {
  errorCountThisMinute = 0;
  minuteResetTimer = null;
};

const flushQueue = async () => {
  if (errorQueue.length === 0) return;

  const batch = errorQueue.splice(0);
  batchTimer = null;

  try {
    await supabase.from('error_log').insert(batch);
  } catch {
    // Silent failure — don't throw if logging fails
  }
};

const scheduleBatch = () => {
  if (batchTimer) return;
  batchTimer = setTimeout(flushQueue, BATCH_INTERVAL_MS);
};

export const logError = (
  source: ErrorSource,
  type: string,
  message: string,
  stackTrace?: string,
  metadata?: Record<string, unknown>
) => {
  // Deduplicate: skip if same type+message logged within 60s
  const dedupeKey = `${type}:${message.slice(0, 200)}`;
  if (recentErrorKeys.has(dedupeKey)) return;
  recentErrorKeys.add(dedupeKey);
  if (!dedupeCleanupTimer) {
    dedupeCleanupTimer = setTimeout(() => {
      recentErrorKeys.clear();
      dedupeCleanupTimer = null;
    }, DEDUP_WINDOW_MS);
  }

  // Rate limit
  if (errorCountThisMinute >= MAX_ERRORS_PER_MINUTE) return;

  errorCountThisMinute++;
  if (!minuteResetTimer) {
    minuteResetTimer = setTimeout(resetMinuteCounter, 60000);
  }

  // Truncate long messages
  const truncatedMessage = message.length > 2000 ? message.slice(0, 2000) : message;
  const truncatedStack = stackTrace && stackTrace.length > 5000 ? stackTrace.slice(0, 5000) : stackTrace;

  errorQueue.push({
    source,
    type,
    message: truncatedMessage,
    stack_trace: truncatedStack || undefined,
    user_id: currentUserId || undefined,
    metadata: metadata || {},
  });

  scheduleBatch();
};

/**
 * Install global error handlers. Call once at app init.
 */
export const installGlobalErrorHandlers = () => {
  window.onerror = (message, source, lineno, colno, error) => {
    logError('frontend', 'unhandled_error', String(message), error?.stack, {
      source,
      lineno,
      colno,
      url: window.location.href,
    });
  };

  window.onunhandledrejection = (event) => {
    const message = event.reason?.message || String(event.reason);
    const stack = event.reason?.stack;

    logError('frontend', 'unhandled_rejection', message, stack, {
      url: window.location.href,
    });
  };
};
